import PropTypes from "prop-types";
import styled from "styled-components";
import { faXmark } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const StyledChip = styled.div`
  font-family: Inter;
  text-align: left;
  width: max-content;
  ${(props) => props.maxWidth && `max-width: ${props.maxWidth};`}
  height: fit-content;
  border-radius: 4px;
  background-color: #dadde1;
  color: #000000;
  user-select: none;
  display: flex;
  overflow-wrap: anywhere;
  align-items: center;
  box-sizing: border-box;

  ${(props) =>
    props.size === "small" &&
    `
      padding-left: 4px;
      padding-right: 4px;
      padding-top: 1px;
      padding-bottom: 1px;
      gap: 2px;
  `}

  ${(props) =>
    props.size === "large" &&
    `
      padding-left: 6px;
      padding-right: 6px;
      padding-top: 2px;
      padding-bottom: 2px;
      gap: 1px;
  `}

  ${(props) =>
    props.size === "xl" &&
    `
      padding: 2px 6px;
      gap: 2px;
  `}

  ${(props) =>
    props.variant === "outlined" &&
    `
      border: 1px solid #979797;
      background-color: #ffffff;
      color: #702A6E;
      box-sizing: border-box;
      cursor: pointer;
    `}

${(props) =>
    props.variant === "actionable" &&
    props.size === "small" &&
    `
  `}

${(props) =>
    props.isDisabled &&
    `
      pointer-events: none;

      &:focus {
        outline: none;
      }
    `}

    ${(props) =>
    props.variant === "actionable" &&
    props.size === "large" &&
    `
        padding-left: 7px;
        padding-bottom: 3px;
        padding-right: 4px;
        padding-top: 3px;
        height: 20px;
      `}
`;

const ChipText = styled.div`
  height: fit-content;
  font-size: 12px;
  line-height: 14px;
  text-size-adjust: none;
  flex: 1 1 auto;
  padding-top: 3px;
  padding-bottom: 3px;
  ${(props) =>
    props.maxWidth &&
    `
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  `}

  ${(props) =>
    props.size === "small" &&
    `
      padding-top: 1px;
      padding-bottom: 1px;
  `}

  ${(props) =>
    props.size === "xl" &&
    `
      font-size: 14px;
  `}
`;

const ChipAction = styled.div`
  padding: 1px;
  cursor: pointer;
  color: #3e3e3c;
  line-height: 10px;
  border-radius: 100%;
  display: flex;
  align-items: center;
  justify-items: center;
  flex: 0 0 16px;

  ${(props) =>
    props.size === "small" &&
    `
      width: 14px;
      height: 14px;
      flex: 0 0 14px;
      margin-left: -1px;
  `}

  ${(props) =>
    props.size === "large" &&
    `
    width: 16px;
    height: 15px;
  `}

  ${(props) =>
    props.size === "xl" &&
    `
    width: 18px;
    height: 17px;
    flex: 0 0 18px;
  `}

  svg {
    width: 100%;
    height: 100%;
  }

  &:hover {
    background-color: #c5c5c7;
  }
`;

const Chip = ({
  text = "",
  variant = "default",
  size = "large",
  maxWidth,
  onDismiss,
  tabIndex,
  isDisabled,
  style,
}) => {
  return (
    <StyledChip
      data-test="chip"
      tabIndex={tabIndex}
      variant={variant}
      size={size}
      maxWidth={maxWidth}
      style={{ ...style }}
      isDisabled={isDisabled}
      // Prevent event from bubbling up to parent components
      // i.e. Combobox (react-select)
      onMouseDown={(event) => event.stopPropagation()}
      onKeyDown={(event) => {
        if (
          ["Backspace", "Delete"].includes(event.key) &&
          variant === "actionable" &&
          onDismiss
        ) {
          onDismiss(event);
        }
      }}
    >
      {variant === "actionable" && onDismiss ? (
        <>
          <ChipText size={size} maxWidth={maxWidth}>
            {text}
          </ChipText>
          <ChipAction size={size}>
            <FontAwesomeIcon icon={faXmark} onClick={onDismiss} />
          </ChipAction>
        </>
      ) : (
        <ChipText title={text} size={size} maxWidth={maxWidth}>
          {text}
        </ChipText>
      )}
    </StyledChip>
  );
};

Chip.propTypes = {
  text: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  variant: PropTypes.oneOf(["default", "actionable", "outlined"]),
  size: PropTypes.oneOf(["small", "large"]),
  onDismiss: PropTypes.func,
};

export default Chip;
